import { Client, Gender } from './Client';
import { User } from './User';

export enum FamilyMemberType {
    client = 'client',
    spouse = 'spouse',
    child = 'child',
}

export interface Insurance {
    _id: string;
    client: any;
    rep: string;
    agent: User | string;
    operator: string;
    status: string;
    isActive: boolean;
    endTime: Date;
    files: string[];
    closedLostReason?: InsuranceClosedLostReasons;
    round: number;
    firstKitSignatureDate: Date;
    appendicesSubmissionDate: Date;
    savingAmount: number;
    sms2010Link: string;
    familyMembers: [
        {
            familyMemberType: FamilyMemberType;
            idNumber: string;
            dateOfBirth: Date;
            firstName: string;
            lastName: string;
            idIssueDate: Date;
            isPoaSigned: boolean;
            errors: string[];
            gender: Gender;
        },
    ];
    lastStatusChangeDate: Date | string;
    lastStatusChanger?: string;
    agentMeetingTime?: Date;
    serviceMeeting?: {
        eventUri: string;
        eventStartTime: Date;
        repId: string;
    };
    insurancePreferredTypes?: string[];
    oneAgentForInsuranceAndPension?: boolean;
}

export interface Polywizz {
    _id: string;
    client: string | Client;
    insurance: string | Insurance;
    familyMemberType: FamilyMemberType;
    clientId: string;
    taskId: string;
    status: InsurancePolywizzStatuses;
    submissionTime: Date;
    policiesSubmissionTime: Date;
    error: string;
    expiryDate: Date;
}

export interface GetPolywizzParams {
    insuranceId?: string;
    clientId?: string;
    clientIds?: string[];
    familyMemberType?: FamilyMemberType;
    status?: InsurancePolywizzStatuses;
    createdAtStart?: string;
    createdAtEnd?: string;
    submissionTimeStart?: string;
    submissionTimeEnd?: string;
    policiesSubmissionTimeStart?: string;
    policiesSubmissionTimeEnd?: string;
    sortBy?: string;
    page?: number;
    limit?: number;
}

export interface SubmitPolywizzParams {
    clientId?: string;
    insuranceId?: string;
    signee?: 'client' | 'spouse';
    polywizzId?: string;
}

export enum InsuranceClosedLostReasons {
    notEligibleDueToChangesInThePastMonth = 'notEligibleDueToChangesInThePastMonth',
    clientIsNotInterested = 'clientIsNotInterested',
    clientDidntScheduleMeeting = 'clientDidntScheduleMeeting',
    noShow = 'noShow',
    missingHafakotSignature = 'missingHafakotSignature',
    lostToCurrentCompany = 'lostToCurrentCompany',
}

export enum InsurancePolywizzStatuses {
    initial = 'initial',
    pending = 'pending',
    timeout = 'timeout',
    temporaryError = 'temporaryError',
    permanentError = 'permanentError',
    mountainDataReceived = 'mountainDataReceived', // waiting for policies...
    policiesError = 'policiesError',
    completed = 'completed', // completed mountain data and policies
    almostCompleted = 'almostCompleted', // waiting for policies for more than 4 days
    completedPoliciesNotRequired = 'completedPoliciesNotRequired', // completed mountain data, client is  and not preferred
    failed = 'failed', // we had an issue somewhere
    reactivationRequired = 'reactivationRequired', // poa is not valid any more
}

export const InsurancePolywizzErrorStatuses = [
    InsurancePolywizzStatuses.temporaryError,
    InsurancePolywizzStatuses.permanentError,
    InsurancePolywizzStatuses.policiesError,
    InsurancePolywizzStatuses.failed,
];

export enum InsuranceRecommendation {
    noChange = 'noChange',
    change = 'change',
    partialCancellation = 'partialCancellation',
    fullCancellation = 'fullCancellation',
}

export enum RecommendationReasons {
    lowerPremium = 'lowerPremium',
    betterCoverage = 'betterCoverage',
    betterCoverageAndLowerPremium = 'betterCoverageAndLowerPremium',
    replacedByAnotherPolicy = 'replacedByAnotherPolicy',
}

export enum InsurancePreferredTypes {
    highRiskPremium = 'highRiskPremium',
    illnessNoHealth = 'illnessNoHealth',
    highTotalPremium = 'highTotalPremium',
    riskNoHealth = 'riskNoHealth',
    highCriticalIllness = 'highCriticalIllness',
    miniHighTotalPremium = 'miniHighTotalPremium',
    noHealthPaysForInsurance = 'noHealthPaysForInsurance',
    noLongTermLifeWithChildren = 'noLongTermLifeWithChildren',
    onlyOneSpouseHasHealth = 'onlyOneSpouseHasHealth',
    doubleInsurance = 'doubleInsurance',
    noHealthInsurance = 'noHealthInsurance',
    marriedWithHealthNoRisk = 'marriedWithHealthNoRisk',
}

export const InsuranceMiniPreferredTypes: string[] = [
    InsurancePreferredTypes.miniHighTotalPremium,
    InsurancePreferredTypes.noHealthPaysForInsurance,
    InsurancePreferredTypes.noLongTermLifeWithChildren,
    InsurancePreferredTypes.onlyOneSpouseHasHealth,
    InsurancePreferredTypes.doubleInsurance,
    InsurancePreferredTypes.noHealthInsurance,
    InsurancePreferredTypes.marriedWithHealthNoRisk,
];
